/**
 * usePremiumPrice — Fetches the Pro subscription price for the user's region.
 *
 * Calls the `subscription-get-pricing` Edge Function which reads the live
 * Stripe Price object. Caches for 5 minutes. Zero hardcoded amounts in the UI.
 *
 * Usage:
 *   const { unitAmount, currency, displayAmount, interval, isLoading } = usePremiumPrice();
 *   // displayAmount → "$19.00" or "R$99,00" depending on region
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { detectCountryFromTimezone } from '@/utils/countryDetection';

export interface PremiumPriceResult {
  priceId: string | null;
  unitAmount: number | null;   // minor units (cents)
  currency: string | null;
  interval: string | null;     // 'month'
  displayAmount: string | null;  // pre-formatted: "$19.00" or "R$99,00"
  isLoading: boolean;
  error: string | null;
}

function detectCountryCode(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  return detectCountryFromTimezone(tz) === 'BR' ? 'BR' : 'US';
}

async function fetchPremiumPrice(country: string): Promise<{
  priceId: string;
  unitAmount: number;
  currency: string;
  interval: string;
  displayAmount: string;
}> {
  const { data, error } = await supabase.functions.invoke('subscription-get-pricing', {
    body: null,
    headers: { 'x-country': country },
  });

  if (error) throw new Error(error.message);
  return data;
}

export function usePremiumPrice(): PremiumPriceResult {
  const country = detectCountryCode();

  const { data, isLoading, error } = useQuery({
    queryKey: ['premium-price', country],
    queryFn: () => fetchPremiumPrice(country),
    staleTime: 5 * 60 * 1_000,
    gcTime:    30 * 60 * 1_000,
    retry: 2,
  });

  return {
    priceId:       data?.priceId ?? null,
    unitAmount:    data?.unitAmount ?? null,
    currency:      data?.currency ?? null,
    interval:      data?.interval ?? null,
    displayAmount: data?.displayAmount ?? null,
    isLoading,
    error:         error instanceof Error ? error.message : null,
  };
}
